// RoomingBoard.jsx — front-desk / MA office-flow board.
// A kanban view over TODAY's appointments, advancing patients through the visit:
//   Waiting → Roomed → With Provider → Checkout  (→ Complete)
// Reuses the existing appointments table (status / room / checked_in_time already
// exist and are writable). The existing Schedule check-in modal sets status
// 'checked-in' + a room, so checked-in patients land in the Waiting column.
// New status strings (waiting/roomed/with-provider/checkout) need no migration —
// status is free text and already in the appointments allowlist write set.

import { useState, useEffect, useCallback } from "react";
import { db } from "../lib/db";
import { fmt12, todayISO } from "../utils/chartHelpers";

const ROOMS = ["Exam 1", "Exam 2", "Exam 3", "Procedure Room", "OR 1", "OR 2", "OR 3"];

const STAGES = [
  { key: "waiting",       label: "Waiting",       statuses: ["waiting", "checked-in"], color: "#0891b2" },
  { key: "roomed",        label: "Roomed",        statuses: ["roomed"],                color: "#0d9488" },
  { key: "with-provider", label: "With Provider", statuses: ["with-provider"],         color: "#7c3aed" },
  { key: "checkout",      label: "Checkout",      statuses: ["checkout"],              color: "#d97706" },
];

// status -> next status when "Advance" is clicked
const ADVANCE = {
  "waiting": "roomed", "checked-in": "roomed", "roomed": "with-provider",
  "with-provider": "checkout", "checkout": "completed",
};
const BACK = { "roomed": "waiting", "with-provider": "roomed", "checkout": "with-provider" };
// Label for the "go back one stage" button — names the destination so it's clear.
const BACK_LABEL = { "roomed": "← Waiting", "with-provider": "← Roomed", "checkout": "← With Provider" };
const ADVANCE_LABEL = {
  "waiting": "Room →", "checked-in": "Room →", "roomed": "To provider →",
  "with-provider": "To checkout →", "checkout": "Complete ✓",
};

export default function RoomingBoard({ onPatientSelect, patients = [] }) {
  const [appts, setAppts] = useState(null);
  const todayStr = todayISO();

  const load = useCallback(() => {
    db.from("appointments").select("*")
      .eq("appointment_date", todayStr)
      .order("appointment_time", { ascending: true })
      .then(({ data }) => {
        const rows = (data || []).map(a => {
          const propPt = patients.find(p => p.id === a.patient_id);
          const name =
            (a.patients?.first_name && a.patients?.last_name) ? `${a.patients.first_name} ${a.patients.last_name}` :
            (propPt?.first_name && propPt?.last_name)         ? `${propPt.first_name} ${propPt.last_name}` :
            (a.patient_name || "Unknown");
          return { ...a, _name: name, _patient: propPt || null };
        });
        setAppts(rows);
      });
  }, [todayStr, patients]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const update = async (appt, patch) => {
    setAppts(rs => (rs || []).map(a => a.id === appt.id ? { ...a, ...patch } : a)); // optimistic
    try { await db.from("appointments").update(patch).eq("id", appt.id); }
    catch (e) { console.error("[rooming] update failed:", e); load(); }
  };
  const advance = (appt) => update(appt, { status: ADVANCE[appt.status] || "roomed" });
  const back    = (appt) => { const p = BACK[appt.status]; if (p) update(appt, { status: p }); };
  const setRoom = (appt, room) => update(appt, { room });

  const minsSince = (iso) => {
    if (!iso) return null;
    const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    return m >= 0 ? m : null;
  };

  if (appts === null) {
    return <div style={{ padding: 40, color: "var(--text-3)", fontSize: 13 }}>Loading rooming board…</div>;
  }

  const scheduledCount = appts.filter(a => a.status === "scheduled").length;
  const completedCount = appts.filter(a => a.status === "completed").length;
  const activeCount    = appts.filter(a => STAGES.some(s => s.statuses.includes(a.status))).length;

  return (
    <div style={{ padding: "4px 2px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 13, color: "var(--text-3)" }}>
          {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
          {"  ·  "}<span style={{ color: "var(--text-2)" }}>{activeCount} in office</span>
          {"  ·  "}{scheduledCount} not arrived  ·  {completedCount} done
        </div>
        <button className="btn btn-secondary btn-sm" onClick={load}>↻ Refresh</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: `repeat(${STAGES.length}, minmax(0, 1fr))`, gap: 12, alignItems: "start" }}>
        {STAGES.map(stage => {
          const items = appts.filter(a => stage.statuses.includes(a.status));
          return (
            <div key={stage.key} style={{ background: "var(--navy-2)", border: "1px solid var(--border-2)", borderRadius: 10, overflow: "hidden", minHeight: 140 }}>
              <div style={{ padding: "10px 12px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: stage.color, flexShrink: 0 }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-1)" }}>{stage.label}</span>
                <span className="badge badge-gray" style={{ marginLeft: "auto" }}>{items.length}</span>
              </div>
              <div style={{ padding: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                {items.length === 0 && (
                  <div style={{ padding: "18px 8px", textAlign: "center", color: "var(--text-3)", fontSize: 12 }}>—</div>
                )}
                {items.map(a => {
                  const waited = minsSince(a.checked_in_time);
                  return (
                    <div key={a.id} style={{ background: "var(--navy-3)", border: "1px solid var(--border)", borderRadius: 8, padding: "9px 10px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
                        <button
                          onClick={() => a._patient && onPatientSelect?.(a._patient)}
                          title={a._patient ? "Open chart" : ""}
                          style={{ background: "none", border: "none", color: "var(--text-1)", fontWeight: 600, fontSize: 13, cursor: a._patient ? "pointer" : "default", padding: 0, textAlign: "left" }}>
                          {a._name}
                        </button>
                        <span style={{ fontSize: 11, color: "var(--text-3)", whiteSpace: "nowrap" }}>{a.appointment_time ? fmt12(a.appointment_time) : ""}</span>
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
                        {a.appointment_type || "Visit"}{a.provider ? ` · ${a.provider}` : ""}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                        <select value={a.room || ""} onChange={e => setRoom(a, e.target.value)}
                          style={{ fontSize: 11, background: "var(--navy-2)", color: "var(--text-2)", border: "1px solid var(--border)", borderRadius: 5, padding: "2px 4px" }}>
                          <option value="">No room</option>
                          {ROOMS.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                        {waited != null && (
                          <span style={{ fontSize: 11, color: waited >= 20 ? "var(--amber)" : "var(--text-3)" }}>{waited}m</span>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                        {BACK[a.status] && (
                          <button className="btn btn-secondary btn-sm" onClick={() => back(a)} style={{ fontSize: 11 }} title={"Move back to " + (BACK[a.status] === "waiting" ? "Waiting" : BACK[a.status] === "roomed" ? "Roomed" : "With Provider")}>
                            {BACK_LABEL[a.status] || "← Back"}
                          </button>
                        )}
                        <button className="btn btn-primary btn-sm" onClick={() => advance(a)} style={{ fontSize: 11, marginLeft: "auto" }}>
                          {ADVANCE_LABEL[a.status] || "Advance →"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {activeCount === 0 && (
        <div style={{ marginTop: 24, textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>
          No patients in office yet. Check patients in from the Schedule to populate the board.
        </div>
      )}
    </div>
  );
}
